import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Doc, Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { FEATURE_FLAG_DEFINITIONS, isKnownFeatureFlag } from "@stoneflow/shared"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useAction, useMutation, useQuery } from "convex/react"
import { AlertTriangle, Flag, Pencil, Plus, Trash2, X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"
import { RouteError } from "#/components/route-error"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card"
import { Checkbox } from "#/components/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "#/components/ui/dialog"
import { Input } from "#/components/ui/input"
import { Label } from "#/components/ui/label"
import { Skeleton } from "#/components/ui/skeleton"
import { Spinner } from "#/components/ui/spinner"
import { Switch } from "#/components/ui/switch"
import { Textarea } from "#/components/ui/textarea"
import { useDebounce } from "#/hooks/useDebounce"
import { trackEvent } from "#/lib/posthog"
import { ROLES } from "#/lib/roles"

export const Route = createFileRoute("/_authenticated/admin/feature-flags")({
	beforeLoad: ({ context }) => {
		const roles = (context as { roles?: string[] }).roles ?? []
		// Super admin only — platform operators can't edit feature flags.
		if (!roles.includes("platform_admin")) {
			throw redirect({ to: "/admin" })
		}
	},
	errorComponent: RouteError,
	component: FeatureFlagsPage,
})

type FeatureFlag = Doc<"featureFlags">

type ResolvedUser = {
	clerkId: string
	firstName: string | null
	lastName: string | null
	email: string | null
	orgName: string | null
	orgId: string | null
}

function formatUserName(u: ResolvedUser) {
	const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.clerkId
	return u.orgName ? `${name} (${u.orgName})` : name
}

function FeatureFlagsPage() {
	const flags = useQuery(api.platform.featureFlags.list)
	const createFlag = useMutation(api.platform.featureFlags.create)
	const updateFlag = useMutation(api.platform.featureFlags.update)
	const removeFlag = useMutation(api.platform.featureFlags.remove)

	const [showCreate, setShowCreate] = useState(false)
	const [editFlag, setEditFlag] = useState<FeatureFlag | null>(null)
	const [deleteId, setDeleteId] = useState<Id<"featureFlags"> | null>(null)

	if (flags === undefined) return <FlagsSkeleton />

	const handleToggle = async (flag: FeatureFlag) => {
		try {
			await updateFlag({ id: flag._id, enabled: !flag.enabled })
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to toggle flag")
		}
	}

	const handleDelete = async () => {
		if (!deleteId) return
		try {
			await removeFlag({ id: deleteId })
			toast.success("Flag deleted")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to delete flag")
		} finally {
			setDeleteId(null)
		}
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div>
						<CardTitle>Feature Flags</CardTitle>
						<CardDescription>
							Manage platform-wide feature flags with optional role, org, and user targeting
						</CardDescription>
					</div>
					<Button size="sm" onClick={() => setShowCreate(true)}>
						<Plus className="mr-1 size-4" />
						Create Flag
					</Button>
				</CardHeader>
				<CardContent>
					{flags.length === 0 ? (
						<div className="flex flex-col items-center gap-2 py-8 text-center">
							<Flag className="size-8 text-foreground-muted" />
							<p className="text-sm text-foreground-secondary">
								No feature flags yet. Create one to get started.
							</p>
						</div>
					) : (
						<div className="space-y-3">
							{flags.map((flag) => (
								<div
									key={flag._id}
									className="flex items-center justify-between rounded-md border border-border p-3"
								>
									<div className="min-w-0 flex-1 space-y-1">
										<div className="flex items-center gap-2">
											<p className="font-medium">{flag.name}</p>
											{!isKnownFeatureFlag(flag.name) && (
												<Badge variant="destructive" className="gap-1">
													<AlertTriangle className="size-3" />
													Unknown
												</Badge>
											)}
											<TargetingBadges flag={flag} />
										</div>
										{flag.description && (
											<p className="text-sm text-foreground-secondary">{flag.description}</p>
										)}
									</div>
									<div className="flex items-center gap-2">
										<Switch checked={flag.enabled} onCheckedChange={() => handleToggle(flag)} />
										<Button variant="ghost" size="icon-sm" onClick={() => setEditFlag(flag)}>
											<span className="sr-only">Edit</span>
											<Pencil className="size-4" />
										</Button>
										<Button variant="ghost" size="icon-sm" onClick={() => setDeleteId(flag._id)}>
											<span className="sr-only">Delete</span>
											<Trash2 className="size-4 text-severe" />
										</Button>
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<FlagFormDialog
				open={showCreate}
				onOpenChange={setShowCreate}
				onSubmit={async (data) => {
					await createFlag(data)
					trackEvent("admin_feature_flag_created", { flag_name: data.name })
					toast.success(`Flag "${data.name}" created`)
					setShowCreate(false)
				}}
			/>

			{editFlag && (
				<FlagFormDialog
					key={editFlag._id}
					open
					onOpenChange={() => setEditFlag(null)}
					initial={editFlag}
					onSubmit={async (data) => {
						await updateFlag({
							id: editFlag._id,
							description: data.description,
							enabled: data.enabled,
							targetingRules: data.targetingRules,
						})
						trackEvent("admin_feature_flag_updated", { flag_name: editFlag.name })
						toast.success(`Flag "${editFlag.name}" updated`)
						setEditFlag(null)
					}}
				/>
			)}

			<Dialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Feature Flag</DialogTitle>
						<DialogDescription>
							This action cannot be undone. The flag will be removed and any code checking it will
							evaluate to false.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteId(null)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete}>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	)
}

function TargetingBadges({ flag }: { flag: FeatureFlag }) {
	const rules = flag.targetingRules
	if (!rules) return null

	return (
		<>
			{rules.roles && rules.roles.length > 0 && (
				<Badge variant="secondary">
					{rules.roles.length} role{rules.roles.length > 1 ? "s" : ""}
				</Badge>
			)}
			{rules.orgIds && rules.orgIds.length > 0 && (
				<Badge variant="secondary">
					{rules.orgIds.length} org{rules.orgIds.length > 1 ? "s" : ""}
				</Badge>
			)}
			{rules.userClerkIds && rules.userClerkIds.length > 0 && (
				<Badge variant="secondary">
					{rules.userClerkIds.length} user{rules.userClerkIds.length > 1 ? "s" : ""}
				</Badge>
			)}
		</>
	)
}

type TargetingRules = {
	userClerkIds?: string[]
	roles?: string[]
	orgIds?: string[]
}

function FlagFormDialog({
	open,
	onOpenChange,
	initial,
	onSubmit,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	initial?: FeatureFlag
	onSubmit: (data: {
		name: string
		description: string
		enabled: boolean
		targetingRules?: TargetingRules
	}) => Promise<void>
}) {
	const [name, setName] = useState(initial?.name ?? "")
	const [description, setDescription] = useState(initial?.description ?? "")
	const [enabled, setEnabled] = useState(initial?.enabled ?? false)
	const [selectedRoles, setSelectedRoles] = useState<string[]>(initial?.targetingRules?.roles ?? [])
	const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>(
		initial?.targetingRules?.orgIds ?? [],
	)
	const [selectedUsers, setSelectedUsers] = useState<ResolvedUser[]>([])
	const [usersLoading, setUsersLoading] = useState(false)
	const [submitting, setSubmitting] = useState(false)

	const isEdit = !!initial
	const resolveUsersAction = useAction(api.platform.adminActions.resolveUsers)

	// Determine which known flags are not yet created
	const existingFlags = useQuery(api.platform.featureFlags.list)
	const existingNames = new Set(existingFlags?.map((f) => f.name) ?? [])
	const availableDefinitions = FEATURE_FLAG_DEFINITIONS.filter((d) => !existingNames.has(d.name))
	const [useCustomName, setUseCustomName] = useState(false)

	// Resolve existing user IDs to display names on mount
	const initialUserIds = initial?.targetingRules?.userClerkIds
	useEffect(() => {
		if (!initialUserIds || initialUserIds.length === 0) return
		setUsersLoading(true)
		resolveUsersAction({ userIds: initialUserIds })
			.then(setSelectedUsers)
			.catch(() => {
				setSelectedUsers(
					initialUserIds.map((id) => ({
						clerkId: id,
						firstName: null,
						lastName: null,
						email: null,
						orgName: null,
						orgId: null,
					})),
				)
			})
			.finally(() => setUsersLoading(false))
	}, [initialUserIds, resolveUsersAction])

	const toggleRole = (role: string) => {
		setSelectedRoles((prev) =>
			prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
		)
	}

	const toggleOrg = (orgId: string) => {
		setSelectedOrgIds((prev) =>
			prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId],
		)
	}

	const addUser = (user: ResolvedUser) => {
		if (selectedUsers.some((u) => u.clerkId === user.clerkId)) return
		setSelectedUsers((prev) => [...prev, user])
	}

	const removeUser = (clerkId: string) => {
		setSelectedUsers((prev) => prev.filter((u) => u.clerkId !== clerkId))
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setSubmitting(true)

		const userClerkIds = selectedUsers.map((u) => u.clerkId)
		const hasTargeting =
			selectedRoles.length > 0 || selectedOrgIds.length > 0 || userClerkIds.length > 0

		const targetingRules: TargetingRules | undefined = hasTargeting
			? {
					...(selectedRoles.length > 0 ? { roles: selectedRoles } : {}),
					...(selectedOrgIds.length > 0 ? { orgIds: selectedOrgIds } : {}),
					...(userClerkIds.length > 0 ? { userClerkIds } : {}),
				}
			: undefined

		try {
			await onSubmit({
				name: name.trim(),
				description: description.trim(),
				enabled,
				targetingRules,
			})
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Operation failed")
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{isEdit ? "Edit Flag" : "Create Feature Flag"}</DialogTitle>
					<DialogDescription>
						{isEdit
							? "Update the flag description, state, and targeting rules."
							: "Define a new feature flag with optional targeting."}
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-5">
					<div className="space-y-2">
						<Label htmlFor="flag-name">Name</Label>
						{isEdit ? (
							<Input id="flag-name" value={name} disabled />
						) : !useCustomName && availableDefinitions.length > 0 ? (
							<div className="space-y-2">
								<select
									id="flag-name"
									className="flex h-9 w-full rounded-md border border-border bg-input-background px-3 py-1 text-sm"
									value={name}
									onChange={(e) => {
										const selected = e.target.value
										setName(selected)
										const def = FEATURE_FLAG_DEFINITIONS.find((d) => d.name === selected)
										if (def && !description) setDescription(def.description)
									}}
								>
									<option value="">Select a flag...</option>
									{availableDefinitions.map((d) => (
										<option key={d.name} value={d.name}>
											{d.name}
										</option>
									))}
								</select>
								<button
									type="button"
									className="text-xs text-foreground-secondary hover:text-foreground"
									onClick={() => setUseCustomName(true)}
								>
									Or enter a custom name
								</button>
							</div>
						) : (
							<div className="space-y-2">
								<Input
									id="flag-name"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="enable.my-feature"
									required
								/>
								{availableDefinitions.length > 0 && (
									<button
										type="button"
										className="text-xs text-foreground-secondary hover:text-foreground"
										onClick={() => {
											setUseCustomName(false)
											setName("")
										}}
									>
										Pick from known flags
									</button>
								)}
							</div>
						)}
					</div>
					<div className="space-y-2">
						<Label htmlFor="flag-desc">Description</Label>
						<Textarea
							id="flag-desc"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="What this flag controls"
							required
						/>
					</div>
					<div className="flex items-center gap-2">
						<Switch checked={enabled} onCheckedChange={setEnabled} id="flag-enabled" />
						<Label htmlFor="flag-enabled">Enabled</Label>
					</div>

					<div className="space-y-4 rounded-md border border-border p-3">
						<p className="text-sm font-medium">Targeting Rules</p>
						<p className="text-xs text-foreground-muted">
							Leave all empty to target all authenticated users when enabled. Matching any rule is
							sufficient (OR logic).
						</p>

						<RoleCheckboxes selected={selectedRoles} onToggle={toggleRole} />
						<OrgCheckboxes selected={selectedOrgIds} onToggle={toggleOrg} />
						<UserTargeting
							selected={selectedUsers}
							loading={usersLoading}
							onAdd={addUser}
							onRemove={removeUser}
						/>
					</div>

					<DialogFooter>
						<Button type="submit" disabled={!name.trim() || !description.trim() || submitting}>
							{submitting ? "Saving..." : isEdit ? "Update" : "Create"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

function RoleCheckboxes({
	selected,
	onToggle,
}: {
	selected: string[]
	onToggle: (role: string) => void
}) {
	return (
		<div className="space-y-2">
			<Label className="text-xs">Roles</Label>
			<div className="flex flex-wrap gap-3">
				{ROLES.map((role) => (
					<div key={role} className="flex items-center gap-1.5 text-sm">
						<Checkbox checked={selected.includes(role)} onCheckedChange={() => onToggle(role)} />
						<span>{role}</span>
					</div>
				))}
			</div>
		</div>
	)
}

function OrgCheckboxes({
	selected,
	onToggle,
}: {
	selected: string[]
	onToggle: (orgId: string) => void
}) {
	const orgs = useQuery(api.platform.tenants.list)

	if (orgs === undefined) {
		return (
			<div className="space-y-2">
				<Label className="text-xs">Organisations</Label>
				<Skeleton className="h-8 w-full" />
			</div>
		)
	}

	if (orgs.length === 0) return null

	return (
		<div className="space-y-2">
			<Label className="text-xs">Organisations</Label>
			<div className="max-h-40 space-y-1.5 overflow-y-auto">
				{orgs.map((org) => (
					<div key={org.orgId} className="flex items-center gap-1.5 text-sm">
						<Checkbox
							checked={selected.includes(org.orgId)}
							onCheckedChange={() => onToggle(org.orgId)}
						/>
						<span>{org.name}</span>
						<Badge variant="secondary" className="ml-auto text-xs">
							{org.plan}
						</Badge>
					</div>
				))}
			</div>
		</div>
	)
}

function UserTargeting({
	selected,
	loading,
	onAdd,
	onRemove,
}: {
	selected: ResolvedUser[]
	loading: boolean
	onAdd: (user: ResolvedUser) => void
	onRemove: (clerkId: string) => void
}) {
	return (
		<div className="space-y-2">
			<Label className="text-xs">Users</Label>
			{loading ? (
				<div className="flex items-center gap-2 text-sm text-foreground-muted">
					<Spinner className="size-3" />
					Loading users...
				</div>
			) : (
				<>
					{selected.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{selected.map((user) => (
								<span
									key={user.clerkId}
									className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs"
								>
									{formatUserName(user)}
									<button
										type="button"
										onClick={() => onRemove(user.clerkId)}
										className="rounded-sm opacity-50 hover:opacity-100"
									>
										<X className="size-3" />
									</button>
								</span>
							))}
						</div>
					)}
					<UserSearchInput excludeIds={selected.map((u) => u.clerkId)} onSelect={onAdd} />
				</>
			)}
		</div>
	)
}

function UserSearchInput({
	excludeIds,
	onSelect,
}: {
	excludeIds: string[]
	onSelect: (user: ResolvedUser) => void
}) {
	const [query, setQuery] = useState("")
	const debouncedQuery = useDebounce(query, 300)
	const [results, setResults] = useState<ResolvedUser[]>([])
	const [searching, setSearching] = useState(false)
	const [showResults, setShowResults] = useState(false)
	const searchUsers = useAction(api.platform.adminActions.searchUsers)

	const doSearch = useCallback(
		async (q: string) => {
			if (!q.trim()) {
				setResults([])
				return
			}
			setSearching(true)
			try {
				const res = await searchUsers({ query: q })
				setResults(res.filter((u) => !excludeIds.includes(u.clerkId)))
			} catch {
				setResults([])
			} finally {
				setSearching(false)
			}
		},
		[searchUsers, excludeIds],
	)

	useEffect(() => {
		doSearch(debouncedQuery)
	}, [debouncedQuery, doSearch])

	return (
		<div className="relative">
			<Input
				value={query}
				onChange={(e) => {
					setQuery(e.target.value)
					setShowResults(true)
				}}
				onFocus={() => setShowResults(true)}
				onBlur={() => {
					// Delay to allow click on result
					setTimeout(() => setShowResults(false), 200)
				}}
				placeholder="Search by name or email..."
				className="h-8 text-sm"
			/>
			{showResults && (query.trim().length > 0 || searching) && (
				<div className="absolute top-full z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
					{searching ? (
						<div className="flex items-center gap-2 p-2 text-sm text-foreground-muted">
							<Spinner className="size-3" />
							Searching...
						</div>
					) : results.length === 0 ? (
						<p className="p-2 text-sm text-foreground-muted">No users found</p>
					) : (
						<div className="max-h-48 overflow-y-auto py-1">
							{results.map((user) => (
								<button
									key={user.clerkId}
									type="button"
									className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent"
									onMouseDown={(e) => {
										e.preventDefault()
										onSelect(user)
										setQuery("")
										setResults([])
									}}
								>
									<span>{formatUserName(user)}</span>
									{user.email && (
										<span className="text-xs text-foreground-muted">{user.email}</span>
									)}
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

function FlagsSkeleton() {
	return (
		<Card>
			<CardHeader>
				<Skeleton className="h-5 w-32" />
			</CardHeader>
			<CardContent className="space-y-3">
				{Array.from({ length: 3 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
					<Skeleton key={i} className="h-16 w-full" />
				))}
			</CardContent>
		</Card>
	)
}
